import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'horizon:wishlist';

function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

function writeWishlist(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (_err) {
    /* storage full or disabled */
  }
  window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
}

function hasInWishlist(handle) {
  if (!handle) return false;
  return readWishlist().includes(handle);
}

function toggleInWishlist(handle) {
  if (!handle) return false;
  const list = readWishlist();
  const idx = list.indexOf(handle);
  if (idx === -1) {
    list.push(handle);
  } else {
    list.splice(idx, 1);
  }
  writeWishlist(list);
  return list.includes(handle);
}

function removeFromWishlist(handle) {
  if (!handle) return;
  writeWishlist(readWishlist().filter((h) => h !== handle));
}

function updateHeaderBadge() {
  const count = readWishlist().length;
  const hasItems = count > 0;

  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = hasItems ? String(count) : '';
    el.classList.toggle('hidden', !hasItems);
  });

  document.querySelectorAll('[data-wishlist-count-badge]').forEach((el) => {
    el.classList.toggle('visually-hidden', !hasItems);
    el.hidden = !hasItems;
  });

  document.querySelectorAll('[data-wishlist-header-link]').forEach((el) => {
    el.classList.toggle('header-actions__wishlist--active', hasItems);
    const defaultLabel = el.getAttribute('data-default-label');
    const withCount = el.getAttribute('data-label-with-count');
    if (hasItems && withCount) {
      el.setAttribute('aria-label', withCount.replace('__COUNT__', String(count)));
    } else if (defaultLabel) {
      el.setAttribute('aria-label', defaultLabel);
    }
  });
}

/** Re-apply wishlist UI after section morph / cart updates (server HTML is always inactive). */
function syncWishlistUi() {
  updateHeaderBadge();
  document.querySelectorAll('wishlist-toggle').forEach((el) => {
    if (el instanceof WishlistToggle) el.sync();
  });
}

function getMoneyFormat(root) {
  return root?.getAttribute('data-money-format') || '{{amount_with_comma_separator}} €';
}

function formatPrice(cents, moneyFormat) {
  if (typeof cents !== 'number') return '';
  const currency = window.Shopify?.currency?.active || 'EUR';
  return formatMoney(cents, moneyFormat, currency);
}

function getDefaultVariant(product) {
  if (!product?.variants?.length) return null;
  return product.variants.find((v) => v.available) || product.variants[0];
}

function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });
  return ids;
}

async function fetchCart() {
  const res = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('cart fetch failed');
  return res.json();
}

async function addVariantsToCart(variantIds) {
  const sectionIds = getCartSectionIds();
  /** @type {{ items: { id: number; quantity: number }[]; sections?: string }} */
  const payload = {
    items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
  };
  if (sectionIds.length) payload.sections = sectionIds.join(',');

  const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const res = await fetch(cartAddUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || 'add failed');
  }
  return res.json();
}

async function addVariantsAndNotifyCart(variantIds) {
  const addResponse = await addVariantsToCart(variantIds);
  const cart = await fetchCart();
  document.dispatchEvent(
    new CartAddEvent(cart, 'wishlist', {
      source: 'wishlist',
      itemCount: cart.item_count,
      sections: addResponse.sections,
    })
  );
  return cart;
}

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('data-product-handle');
    this.button = this.querySelector('button');
    if (!this.button) return;

    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this.button.addEventListener('click', this._onClick);
    window.addEventListener('wishlist:change', this._onChange);
    this._sync();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this._onClick);
    window.removeEventListener('wishlist:change', this._onChange);
  }

  _onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const isActive = toggleInWishlist(this.handle);
    this._setActive(isActive);
  }

  _onChange() {
    this.sync();
  }

  /** @public */
  sync() {
    this._sync();
  }

  _sync() {
    this._setActive(hasInWishlist(this.handle));
  }

  _setActive(active) {
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.classList.toggle('is-active', active);
    const label = active
      ? this.getAttribute('data-label-remove') || 'Quitar de favoritos'
      : this.getAttribute('data-label-add') || 'Añadir a favoritos';
    this.button.setAttribute('aria-label', label);
    const labelEl = this.button.querySelector('[data-wishlist-label]');
    if (labelEl) labelEl.textContent = label;
  }
}

class WishlistList extends HTMLElement {
  connectedCallback() {
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.itemsContainer = this.querySelector('[data-wishlist-items]');
    this.footer = this.querySelector('[data-wishlist-footer]');
    this.addAllBtn = this.querySelector('[data-add-all-to-cart]');
    this.addAllHint = this.querySelector('[data-add-all-hint]');
    this.countEl = this.querySelector('[data-wishlist-count-label]');
    this.template = this.querySelector('[data-wishlist-item-template]');
    this.moneyFormat = getMoneyFormat(this);
    this.closeIconHtml = this.querySelector('[data-close-icon-source]')?.innerHTML.trim() || '';
    this.cartIconHtml = this.querySelector('[data-cart-icon-source]')?.innerHTML.trim() || '';

    this._render = this._render.bind(this);
    this._onAddAll = this._onAddAll.bind(this);
    window.addEventListener('wishlist:change', this._render);
    if (this.addAllBtn) this.addAllBtn.addEventListener('click', this._onAddAll);
    this._render();
    updateHeaderBadge();
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:change', this._render);
    if (this.addAllBtn) this.addAllBtn.removeEventListener('click', this._onAddAll);
  }

  _formatCount(n) {
    if (n === 1) {
      return this.getAttribute('data-count-one') || '1 producto';
    }
    const tpl = this.getAttribute('data-count-many') || '{{ count }} productos';
    return tpl.replace('{{ count }}', String(n));
  }

  _configureRowCartButton(row, product, cartBtn) {
    if (!cartBtn) return;

    const controls = row.querySelector('.wishlist-row__controls');
    const hasVariantSelector = (product?.variants?.length ?? 0) > 1;
    const addLabel = this.getAttribute('data-add-label') || 'Añadir al carrito';
    const labelEl = cartBtn.querySelector('[data-wishlist-cart-label]');
    const iconWrap = cartBtn.querySelector('.wishlist-row__cart-icon');

    controls?.classList.toggle('wishlist-row__controls--no-selector', !hasVariantSelector);
    cartBtn.classList.toggle('wishlist-row__cart--full', !hasVariantSelector);

    if (!hasVariantSelector) {
      if (labelEl) {
        labelEl.textContent = addLabel;
        labelEl.hidden = false;
      }
      if (iconWrap) iconWrap.hidden = true;
      cartBtn.setAttribute('aria-label', addLabel);
    } else {
      if (labelEl) labelEl.hidden = true;
      if (iconWrap) iconWrap.hidden = false;
      cartBtn.setAttribute('aria-label', addLabel);
    }
  }

  _setupVariantSelect(select, product) {
    const placeholder = this.getAttribute('data-variant-placeholder') || 'Selecciona tu talla';
    select.innerHTML = '';

    if (!product?.variants?.length) {
      select.hidden = true;
      return;
    }

    if (product.variants.length === 1) {
      select.hidden = true;
      select.dataset.variantId = String(product.variants[0].id);
      return;
    }

    select.hidden = false;
    delete select.dataset.variantId;

    const empty = document.createElement('option');
    empty.value = '';
    empty.textContent = placeholder;
    select.appendChild(empty);

    product.variants.forEach((variant) => {
      const opt = document.createElement('option');
      opt.value = String(variant.id);
      opt.textContent = variant.title;
      if (!variant.available) {
        opt.disabled = true;
        opt.textContent += ' — Agotado';
      }
      select.appendChild(opt);
    });
  }

  _getRowVariantId(row, product) {
    const select = row.querySelector('[data-wishlist-variant-select]');
    if (!select) return null;

    if (product.variants.length === 1) {
      return product.variants[0].available ? product.variants[0].id : null;
    }

    const val = select.value;
    if (!val) return null;
    const variant = product.variants.find((v) => String(v.id) === val);
    return variant?.available ? variant.id : null;
  }

  _updateAddOneState(row, product) {
    const btn = row.querySelector('[data-wishlist-add-one]');
    const select = row.querySelector('[data-wishlist-variant-select]');
    if (!btn) return;

    const variantId = this._getRowVariantId(row, product);
    btn.disabled = !variantId;

    if (select && !select.hidden) {
      const onChange = () => {
        btn.disabled = !this._getRowVariantId(row, product);
      };
      select.removeEventListener('change', select._wishlistChange);
      select._wishlistChange = onChange;
      select.addEventListener('change', onChange);
      onChange();
    }
  }

  async _onAddAll() {
    const rows = [...this.querySelectorAll('[data-wishlist-row]')];
    const variantIds = [];
    let missing = 0;

    for (const row of rows) {
      const handle = row.dataset.productHandle;
      const product = this._productsByHandle?.[handle];
      if (!product) continue;
      const id = this._getRowVariantId(row, product);
      if (id) variantIds.push(id);
      else if (product.variants?.length > 1) missing += 1;
    }

    if (variantIds.length === 0) {
      if (this.addAllHint) {
        this.addAllHint.textContent =
          missing > 0
            ? 'Selecciona la talla u opción en cada producto antes de continuar.'
            : 'No hay productos disponibles para añadir.';
        this.addAllHint.hidden = false;
      }
      return;
    }

    if (this.addAllHint) this.addAllHint.hidden = true;
    if (this.addAllBtn) this.addAllBtn.disabled = true;

    try {
      await addVariantsAndNotifyCart(variantIds);
      if (this.addAllHint) {
        this.addAllHint.textContent =
          variantIds.length === 1
            ? '1 producto añadido al carrito.'
            : `${variantIds.length} productos añadidos al carrito.`;
        this.addAllHint.hidden = false;
        this.addAllHint.classList.add('wishlist-page__add-all-hint--success');
        this.addAllHint.classList.remove('wishlist-page__add-all-hint--error');
      }
    } catch (_e) {
      if (this.addAllHint) {
        this.addAllHint.textContent = 'No se pudieron añadir todos los productos. Revisa las opciones.';
        this.addAllHint.hidden = false;
        this.addAllHint.classList.add('wishlist-page__add-all-hint--error');
        this.addAllHint.classList.remove('wishlist-page__add-all-hint--success');
      }
    } finally {
      if (this.addAllBtn) this.addAllBtn.disabled = false;
    }
  }

  async _render() {
    const handles = readWishlist();
    if (!this.itemsContainer || !this.template) return;

    this.itemsContainer.innerHTML = '';
    this._productsByHandle = {};

    if (this.countEl) {
      this.countEl.textContent = handles.length ? this._formatCount(handles.length) : '';
    }

    const showList = handles.length > 0;
    if (this.emptyState) this.emptyState.hidden = showList;
    if (this.itemsContainer) this.itemsContainer.hidden = !showList;
    if (this.footer) this.footer.hidden = !showList;
    if (this.addAllHint) {
      this.addAllHint.hidden = true;
      this.addAllHint.classList.remove(
        'wishlist-page__add-all-hint--success',
        'wishlist-page__add-all-hint--error'
      );
    }

    if (!showList) {
      updateHeaderBadge();
      return;
    }

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${encodeURIComponent(handle)}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    );

    products.forEach((product, index) => {
      const handle = handles[index];
      const node = this.template.content.cloneNode(true);
      const row = node.querySelector('[data-wishlist-row]');
      if (!row) return;

      row.dataset.productHandle = handle;

      const removeBtn = node.querySelector('[data-wishlist-remove]');
      if (removeBtn) {
        const iconWrap = removeBtn.querySelector('.svg-wrapper');
        if (iconWrap) iconWrap.innerHTML = this.closeIconHtml;
        removeBtn.addEventListener('click', () => removeFromWishlist(handle));
      }

      const cartBtn = node.querySelector('[data-wishlist-add-one]');
      if (cartBtn) {
        const iconWrap = cartBtn.querySelector('.wishlist-row__cart-icon');
        if (iconWrap) iconWrap.innerHTML = this.cartIconHtml;
      }

      if (!product) {
        row.classList.add('wishlist-row--unavailable');
        const title = node.querySelector('[data-wishlist-title]');
        if (title) title.textContent = handle;
        node.querySelector('.wishlist-row__controls')?.remove();
        if (cartBtn) cartBtn.remove();
        this.itemsContainer.appendChild(node);
        return;
      }

      this._productsByHandle[handle] = product;

      node.querySelectorAll('[data-wishlist-link]').forEach((a) => {
        a.href = product.url;
      });

      const image = node.querySelector('[data-wishlist-image]');
      if (image && product.featured_image) {
        image.src = product.featured_image;
        image.alt = product.title;
      } else if (image) {
        image.remove();
      }

      const vendor = node.querySelector('[data-wishlist-vendor]');
      if (vendor) vendor.textContent = product.vendor || '';

      const title = node.querySelector('[data-wishlist-title]');
      if (title) title.textContent = product.title;

      const onSale = product.compare_at_price && product.compare_at_price > product.price;
      const price = node.querySelector('[data-wishlist-price]');
      const compareAt = node.querySelector('[data-wishlist-compare]');
      if (price) {
        price.textContent = formatPrice(
          onSale ? product.price : product.price,
          this.moneyFormat
        );
        price.classList.toggle('wishlist-row__price--sale', onSale);
      }
      if (compareAt) {
        if (onSale) {
          compareAt.textContent = formatPrice(product.compare_at_price, this.moneyFormat);
        } else {
          compareAt.remove();
        }
      }

      const select = node.querySelector('[data-wishlist-variant-select]');
      if (select) this._setupVariantSelect(select, product);
      if (cartBtn) this._configureRowCartButton(row, product, cartBtn);

      if (cartBtn) {
        cartBtn.addEventListener('click', async () => {
          const variantId = this._getRowVariantId(row, product);
          if (!variantId) return;
          cartBtn.disabled = true;
          try {
            await addVariantsAndNotifyCart([variantId]);
            if (this.addAllHint) {
              this.addAllHint.textContent = 'Producto añadido al carrito.';
              this.addAllHint.hidden = false;
              this.addAllHint.classList.add('wishlist-page__add-all-hint--success');
              this.addAllHint.classList.remove('wishlist-page__add-all-hint--error');
            }
          } catch (_e) {
            if (this.addAllHint) {
              this.addAllHint.textContent = 'No se pudo añadir al carrito. Prueba otra talla o opción.';
              this.addAllHint.hidden = false;
              this.addAllHint.classList.add('wishlist-page__add-all-hint--error');
              this.addAllHint.classList.remove('wishlist-page__add-all-hint--success');
            }
          } finally {
            cartBtn.disabled = false;
          }
        });
      }

      this.itemsContainer.appendChild(node);
      this._updateAddOneState(row, product);
    });

    updateHeaderBadge();
  }
}

if (!customElements.get('wishlist-toggle')) {
  customElements.define('wishlist-toggle', WishlistToggle);
}
if (!customElements.get('wishlist-list')) {
  customElements.define('wishlist-list', WishlistList);
}

document.addEventListener('DOMContentLoaded', updateHeaderBadge);
window.addEventListener('wishlist:change', updateHeaderBadge);

document.addEventListener(ThemeEvents.cartUpdate, () => {
  queueMicrotask(syncWishlistUi);
});
document.addEventListener(ThemeEvents.variantUpdate, () => {
  queueMicrotask(syncWishlistUi);
});

export { readWishlist, hasInWishlist, toggleInWishlist, removeFromWishlist };
